import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  adminExportHelpMessages,
  adminListHelpMessages,
  adminUpdateHelpMessageStatus,
} from "../../../api/adminSupportApi";

const ACTOR_TABS = [
  { key: "USER", label: "Users" },
  { key: "OWNER", label: "Owners" },
  { key: "DRIVER", label: "Drivers" },
];

const STATUS_OPTIONS = ["ALL", "PENDING", "RESOLVED"];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(v) {
  return v == null ? "" : String(v);
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return safeStr(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "support-help-export.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

function parseRequirementMessage(message) {
  const raw = safeStr(message);
  const lines = raw.split(/\r?\n/);
  const findValue = (prefix) => {
    const line = lines.find((entry) => entry.toLowerCase().startsWith(prefix.toLowerCase()));
    return line ? line.slice(prefix.length).trim() : "";
  };

  const detailsIndex = lines.findIndex(
    (entry) => entry.trim().toLowerCase() === "requirement details:"
  );
  const details =
    detailsIndex >= 0
      ? lines
          .slice(detailsIndex + 1)
          .join("\n")
          .trim()
      : raw.trim();

  return {
    isRequirement: raw.toLowerCase().includes("requirement source:"),
    source: findValue("Requirement source:"),
    dateRequired: findValue("Date required:"),
    pickupLocation: findValue("Pickup location:"),
    details,
  };
}

function StatusPill({ status }) {
  const normalized = safeStr(status).toUpperCase();
  const isResolved = normalized === "RESOLVED";
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold tracking-wide",
        isResolved
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-200 bg-amber-50 text-amber-900"
      )}
    >
      <span
        className={classNames("h-2 w-2 rounded-full", isResolved ? "bg-emerald-500" : "bg-amber-500")}
        aria-hidden="true"
      />
      {normalized || "PENDING"}
    </span>
  );
}

function SummaryCard({ label, value, tone = "default" }) {
  const toneClass =
    tone === "accent"
      ? "border-slate-900 bg-slate-900 text-white"
      : "border-slate-200 bg-white text-slate-900";

  return (
    <div className={classNames("rounded-2xl border px-4 py-4", toneClass)}>
      <div className={classNames("text-[11px] font-semibold uppercase tracking-wider", tone === "accent" ? "text-white/70" : "text-slate-500")}>
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
}

function Pagination({ page, size, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / (Number(size) || 1)));
  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
      <div className="text-xs text-slate-500">
        {total
          ? `Showing ${page * size + 1}-${Math.min(total, (page + 1) * size)} of ${total}`
          : "No submissions"}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page <= 0}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          Prev
        </button>
        <div className="text-sm font-semibold text-slate-700">
          Page {Math.min(totalPages, page + 1)} of {totalPages}
        </div>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default function SupportHelpDesk() {
  const [actorType, setActorType] = useState("USER");
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [requirementsOnly, setRequirementsOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(30);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [actorType, status, q, requirementsOnly]);

  useEffect(() => {
    const currentSeq = ++fetchSeqRef.current;
    setLoading(true);
    setError("");

    const timer = setTimeout(async () => {
      try {
        const data = await adminListHelpMessages({
          actorType,
          status,
          requirementsOnly,
          q,
          page,
          size,
        });
        if (currentSeq !== fetchSeqRef.current) return;
        const nextRows = Array.isArray(data?.rows) ? data.rows : [];
        setRows(nextRows);
        setTotal(Number(data?.total || 0));
        if (nextRows.length && !nextRows.some((row) => row.id === selectedId)) {
          setSelectedId(nextRows[0].id);
        }
        if (!nextRows.length) {
          setSelectedId(null);
        }
      } catch (e) {
        if (currentSeq !== fetchSeqRef.current) return;
        setRows([]);
        setTotal(0);
        setSelectedId(null);
        setError(e?.message || "Failed to load submissions");
      } finally {
        if (currentSeq === fetchSeqRef.current) setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [actorType, status, q, requirementsOnly, page, size, selectedId]);

  const decoratedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        requirement: parseRequirementMessage(row.message),
      })),
    [rows]
  );

  const selectedRow = useMemo(
    () => decoratedRows.find((row) => row.id === selectedId) || decoratedRows[0] || null,
    [decoratedRows, selectedId]
  );

  useEffect(() => {
    setNoteDraft(selectedRow?.adminResolutionNote || "");
  }, [selectedRow?.id, selectedRow?.adminResolutionNote]);

  const summary = useMemo(() => {
    const pending = decoratedRows.filter((row) => safeStr(row.status).toUpperCase() !== "RESOLVED").length;
    const resolved = decoratedRows.filter((row) => safeStr(row.status).toUpperCase() === "RESOLVED").length;
    return {
      visible: decoratedRows.length,
      pending,
      resolved,
    };
  }, [decoratedRows]);

  const emptyText = useMemo(() => {
    if (loading) return "Loading submissions...";
    if (error) return error;
    return requirementsOnly
      ? "No requirements found for this filter."
      : "No help messages found for this filter.";
  }, [loading, error, requirementsOnly]);

  async function handleUpdateStatus(row, nextStatus) {
    if (!row?.id) return;
    try {
      setUpdatingId(row.id);
      setError("");
      const updated = await adminUpdateHelpMessageStatus(row.id, {
        status: nextStatus,
        adminResolutionNote: noteDraft,
      });
      setRows((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (e) {
      setError(e?.message || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleExport() {
    try {
      setExporting(true);
      setError("");
      const { blob, filename } = await adminExportHelpMessages({
        actorType,
        status,
        requirementsOnly,
        q,
      });
      downloadBlob(blob, filename);
    } catch (e) {
      setError(e?.message || "Failed to export submissions");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="min-w-0">
      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          label={requirementsOnly ? "Requirement inbox" : "Help inbox"}
          value={total}
          tone="accent"
        />
        <SummaryCard label="Pending on page" value={summary.pending} />
        <SummaryCard label="Resolved on page" value={summary.resolved} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_380px]">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-slate-500">
                    Support Inbox
                  </div>
                  <h3 className="mt-1 text-xl font-bold text-slate-900">
                    {requirementsOnly ? "Submitted requirements" : "Help messages"}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Review support messages from users, owners, and drivers, track resolution, and export filtered rows.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exporting}
                  className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {exporting ? "Exporting..." : "Export for Excel"}
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {ACTOR_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActorType(tab.key)}
                    className={classNames(
                      "rounded-lg border px-4 py-2 text-sm font-semibold transition",
                      actorType === tab.key
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={
                    requirementsOnly
                      ? "Search by id, name, phone, pickup, date or requirement details"
                      : "Search by help id, actor id, name, phone or message"
                  }
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400"
                />
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option === "ALL" ? "All statuses" : option}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setRequirementsOnly((prev) => !prev)}
                  className={classNames(
                    "rounded-xl border px-4 py-3 text-sm font-semibold transition",
                    requirementsOnly
                      ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-700"
                  )}
                >
                  {requirementsOnly ? "Requirements only" : "All help messages"}
                </button>
              </div>
            </div>
          </div>

          <div className="divide-y divide-slate-200">
            {decoratedRows.length === 0 ? (
              <div className="px-6 py-16 text-center text-sm font-semibold text-slate-500">
                {emptyText}
              </div>
            ) : (
              decoratedRows.map((row) => {
                const selected = row.id === selectedRow?.id;
                const parsed = row.requirement;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedId(row.id)}
                    className={classNames(
                      "w-full px-5 py-4 text-left transition",
                      selected ? "bg-slate-50" : "bg-white hover:bg-slate-50"
                    )}
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
                          <span>#{row.id}</span>
                          <span>{row.actorType || "-"}</span>
                          <span>{formatDateTime(row.createdAt)}</span>
                          {parsed.isRequirement && parsed.source ? <span>{parsed.source}</span> : null}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-sm font-bold text-slate-900">{row.actorName || "-"}</span>
                          <span className="text-sm text-slate-600">{row.actorPhoneNumber || "-"}</span>
                        </div>
                        {parsed.isRequirement ? (
                          <div className="mt-3 grid gap-2 md:grid-cols-2">
                            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                Date required
                              </div>
                              <div className="mt-1 text-sm font-semibold text-slate-900">
                                {parsed.dateRequired || "-"}
                              </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                Pickup location
                              </div>
                              <div className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">
                                {parsed.pickupLocation || "-"}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <div className="mt-3 line-clamp-3 whitespace-pre-wrap break-words text-sm text-slate-700">
                          {parsed.details || row.message || "-"}
                        </div>
                      </div>

                      <div className="flex flex-col items-start gap-2 lg:items-end">
                        <StatusPill status={row.status} />
                        <div className="text-xs text-slate-500">
                          {row.resolvedByAdminId
                            ? `Resolved by admin ${row.resolvedByAdminId}`
                            : "Awaiting action"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <Pagination page={page} size={size} total={total} onPageChange={setPage} />
        </div>

        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-5">
            <div className="text-[12px] font-semibold uppercase tracking-wider text-slate-500">
              Submission detail
            </div>
            <h3 className="mt-1 text-lg font-bold text-slate-900">
              {selectedRow ? `Submission #${selectedRow.id}` : "Select a submission"}
            </h3>
          </div>

          {!selectedRow ? (
            <div className="px-5 py-10 text-sm text-slate-500">
              Choose a row from the inbox to review full details and update status.
            </div>
          ) : (
            <div className="space-y-5 px-5 py-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Actor</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {selectedRow.actorName || "-"}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {selectedRow.actorType || "-"} • {selectedRow.actorId || "-"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Contact</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {selectedRow.actorPhoneNumber || "-"}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Submitted {formatDateTime(selectedRow.createdAt)}
                  </div>
                </div>
              </div>

              {selectedRow.requirement.isRequirement ? (
                <div className="grid gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Date required</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {selectedRow.requirement.dateRequired || "-"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pickup location</div>
                    <div className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-900">
                      {selectedRow.requirement.pickupLocation || "-"}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {selectedRow.requirement.isRequirement ? "Requirement details" : "Message"}
                </div>
                <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">
                  {selectedRow.requirement.details || selectedRow.message || "-"}
                </div>
              </div>

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Admin resolution note
                </label>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add internal note or resolution summary"
                  rows={5}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <StatusPill status={selectedRow.status} />
                <button
                  type="button"
                  onClick={() =>
                    handleUpdateStatus(
                      selectedRow,
                      safeStr(selectedRow.status).toUpperCase() === "RESOLVED"
                        ? "PENDING"
                        : "RESOLVED"
                    )
                  }
                  disabled={updatingId === selectedRow.id}
                  className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {updatingId === selectedRow.id
                    ? "Saving..."
                    : safeStr(selectedRow.status).toUpperCase() === "RESOLVED"
                    ? "Mark pending"
                    : "Mark resolved"}
                </button>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-6 text-slate-600">
                <div>Updated: {formatDateTime(selectedRow.updatedAt)}</div>
                <div>Resolved at: {formatDateTime(selectedRow.resolvedAt)}</div>
                <div>Resolved by admin: {selectedRow.resolvedByAdminId || "-"}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
